/**
 * MagicLinkWidget — one-click generator for a 24-hour signed reviewer URL.
 *
 * Only rendered when the signed-in role is admin or reviewer (the two roles
 * permitted to mint links). Admin can pick target role; reviewer is locked
 * to reviewer-only links. Result URL is copied to clipboard on success.
 *
 * Backend contract:
 *   POST /api/phase4/access/magic-link   {role, base_url}
 *     → 200 { url, token, role, role_label, expires_at, expires_in_seconds }
 *     → 403 if caller not admin/reviewer, or target role not allowed
 */
import React, { useState } from 'react';
import { KeyRound, Copy, Check, AlertCircle, Loader2, ExternalLink } from 'lucide-react';

const ROLE_LABELS = {
  reviewer: 'Avaganz / Full reviewer',
  board_pack: 'Board Pack reviewer',
  buyer: 'Buyer-demo viewer',
};

export default function MagicLinkWidget({ me }) {
  const isAdmin = me?.role === 'admin';
  const canMint = me?.role === 'admin' || me?.role === 'reviewer';

  const [target, setTarget] = useState('reviewer');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // {url, expires_at, role_label}
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!canMint) return null;

  const generate = async () => {
    setLoading(true);
    setError(null);
    setCopied(false);
    try {
      const resp = await fetch('/api/phase4/access/magic-link', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: isAdmin ? target : 'reviewer',
          base_url: window.location.origin,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || `HTTP ${resp.status}`);
      setResult(data);
      // Attempt clipboard copy right away
      try {
        await navigator.clipboard.writeText(data.url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (_e) { /* user can still copy from the textarea */ }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const copyAgain = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* noop */ }
  };

  return (
    <div
      className="liquid-card !p-4 border border-white/8"
      data-testid="magic-link-widget"
    >
      <div className="flex items-center gap-2 mb-2">
        <KeyRound className="w-4 h-4 text-[var(--accent-cyan)]" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-[var(--accent-cyan)]">
          Share reviewer access
        </span>
      </div>
      <div className="text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Copy Reviewer Kit URL
      </div>
      <p className="text-xs text-[var(--ink-secondary)] mt-1 leading-relaxed max-w-2xl">
        Generate a signed 24-hour link so a colleague can open the reviewer
        portal in one click — no access code entry required. Link is stored
        only in your clipboard.
      </p>

      {/* Role picker (admin only) */}
      {isAdmin && (
        <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="magic-link-role-picker">
          {['reviewer', 'board_pack', 'buyer'].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setTarget(r)}
              className={
                'liquid-pill text-[10px] transition-colors ' +
                (target === r
                  ? 'liquid-pill--info !text-white'
                  : 'hover:text-white opacity-70')
              }
              data-testid={`magic-link-role-${r}`}
            >
              {ROLE_LABELS[r]}
            </button>
          ))}
        </div>
      )}

      {/* Generate button */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={generate}
          disabled={loading}
          className="liquid-btn liquid-btn--primary text-xs disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="magic-link-generate"
        >
          {loading ? (
            <>
              <Loader2 className="w-3 h-3 mr-1.5 inline animate-spin" />
              Generating…
            </>
          ) : (
            <>
              <KeyRound className="w-3 h-3 mr-1.5 inline" />
              {result ? 'Generate new link' : `Generate 24 h ${isAdmin ? ROLE_LABELS[target] : 'reviewer'} link`}
            </>
          )}
        </button>
        {result && (
          <span className="text-[10px] text-[var(--ink-tertiary)] font-mono" data-testid="magic-link-expiry">
            expires {new Date(result.expires_at).toLocaleString()}
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <div
          className="mt-3 flex items-start gap-2 text-xs text-[var(--status-danger-text)]"
          data-testid="magic-link-error"
        >
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className="mt-3 space-y-2">
          <textarea
            readOnly
            value={result.url}
            rows={2}
            className="w-full liquid-input text-[11px] font-mono resize-none break-all"
            onFocus={(e) => e.currentTarget.select()}
            data-testid="magic-link-url"
          />
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={copyAgain}
              className="liquid-btn text-xs"
              data-testid="magic-link-copy"
            >
              {copied ? (
                <>
                  <Check className="w-3 h-3 mr-1.5 inline text-[var(--status-success-text)]" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3 h-3 mr-1.5 inline" />
                  Copy again
                </>
              )}
            </button>
            <a
              href={result.url}
              target="_blank"
              rel="noreferrer"
              className="liquid-btn text-xs"
              data-testid="magic-link-open"
            >
              <ExternalLink className="w-3 h-3 mr-1.5 inline" />
              Open in new tab
            </a>
            <span className="text-[10px] text-[var(--ink-tertiary)]">
              Target: <strong className="text-[var(--ink-secondary)]">{result.role_label}</strong>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
